"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  advance,
  emptyMeter,
  levelFromDb,
  type Meter,
  rmsOf,
  type Settings,
  toDb,
} from "@/lib/tool-kit/games-organizers/noise-meter";

export type MeterStatus =
  | "idle"
  | "asking"
  | "listening"
  | "denied"
  | "unsupported"
  | "failed";

const TICK_MS = 66;
const FFT_SIZE = 2048;

interface Engine {
  stream: MediaStream;
  context: AudioContext;
  timer: number;
}

function shutDown(engine: Engine | null): void {
  if (!engine) {
    return;
  }
  window.clearInterval(engine.timer);
  for (const track of engine.stream.getTracks()) {
    track.onended = null;
    track.stop();
  }
  engine.context.close().catch(() => undefined);
}

function isDenied(error: unknown): boolean {
  const name = error instanceof DOMException ? error.name : "";
  return name === "NotAllowedError" || name === "SecurityError";
}

/**
 * Read the microphone level, only after `start` is called from a button press.
 *
 * The audio is analysed in a Web Audio graph that ends at the analyser: it is never connected
 * to the speakers, never recorded and never sent anywhere. Each tick reads one block of
 * samples, reduces it to a level and discards it.
 */
export function useNoiseMeter(settings: Settings) {
  const [status, setStatus] = useState<MeterStatus>("idle");
  const [meter, setMeter] = useState<Meter>(emptyMeter);
  const live = useRef(settings);
  const engine = useRef<Engine | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    live.current = settings;
  }, [settings]);

  const stop = useCallback(() => {
    shutDown(engine.current);
    engine.current = null;
    setStatus((current) => (current === "listening" ? "idle" : current));
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      shutDown(engine.current);
      engine.current = null;
    };
  }, []);

  const start = useCallback(async () => {
    const supported =
      typeof navigator.mediaDevices?.getUserMedia === "function" &&
      typeof AudioContext !== "undefined";
    if (!supported) {
      setStatus("unsupported");
      return;
    }
    shutDown(engine.current);
    engine.current = null;
    setStatus("asking");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      const context = new AudioContext();
      if (!mounted.current) {
        shutDown({ stream, context, timer: 0 });
        return;
      }
      await context.resume();
      const analyser = context.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      context.createMediaStreamSource(stream).connect(analyser);
      const buffer = new Float32Array(analyser.fftSize);
      let last = performance.now();
      const timer = window.setInterval(() => {
        const now = performance.now();
        const dt = now - last;
        last = now;
        analyser.getFloatTimeDomainData(buffer);
        const level = levelFromDb(
          toDb(rmsOf(buffer)),
          live.current.sensitivity
        );
        setMeter((current) => advance(current, level, dt, live.current));
      }, TICK_MS);
      for (const track of stream.getAudioTracks()) {
        track.onended = () => {
          shutDown(engine.current);
          engine.current = null;
          setStatus("failed");
        };
      }
      engine.current = { stream, context, timer };
      setStatus("listening");
    } catch (error) {
      setStatus(isDenied(error) ? "denied" : "failed");
    }
  }, []);

  const reset = useCallback(() => setMeter(emptyMeter()), []);

  return { status, meter, start, stop, reset };
}
